import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import AgeBarChart from './AgeBarChart'
import { Loader } from 'lucide-react'

export interface GeographicLocationDisplay {
  name: string
  users: number
  percentage: number
  flag: string
}

export interface AgeChartDisplay {
  ageRange: string
  users: number
}

const GeographicItem = ({
  location,
}: {
  location: GeographicLocationDisplay
}) => (
  <div className="flex items-center gap-3 p-5 bg-white border border-ss-neutral-50 rounded-xl">
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-sm text-ss-neutral-950 leading-4.5 ">
        {location.name}
      </p>
    </div>
    <div className="text-right whitespace-nowrap">
      <p className="font-bold text-base text-ss-neutral-950 leading-[22px]">
        {location.users.toLocaleString()}
      </p>
      <p className="font-normal text-xs text-ss-neutral-600 leading-4">
        {location.percentage}%
      </p>
    </div>
  </div>
)

interface DemographicsDistributionCardProps {
  isLoading?: boolean
  isError?: boolean
  geographicList: GeographicLocationDisplay[]
  ageChartData: AgeChartDisplay[]
}

export const DemographicsDistributionCard = ({
  isLoading = false,
  isError = false,
  geographicList,
  ageChartData,
}: DemographicsDistributionCardProps) => {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {/* Geographic Distribution */}
      <Card className="bg-white border border-ss-neutral-50 rounded-xl gap-6 p-4 shadow-none">
        <CardHeader className="p-0 gap-1">
          <CardTitle className="text-sm font-bold text-ss-neutral-950 leading-4.5 ">
            Geographic Distribution
          </CardTitle>
          <CardDescription className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
            Where your audience is located
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 p-0">
          {isLoading && (
            <div className="flex items-center justify-center py-36">
              <Loader className="animate-spin text-ss-neutral-500" />
            </div>
          )}
          {!isLoading && !isError && (
            <div className="grid grid-cols-2 gap-3">
              {geographicList.length === 0 ? (
                <p className="text-sm text-ss-neutral-500 col-span-2 flex justify-center py-36">
                  No geographic data available.
                </p>
              ) : (
                geographicList.map((location, index) => (
                  <GeographicItem key={index} location={location} />
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Age Distribution */}
      <Card className="bg-white border border-ss-neutral-50 rounded-xl gap-6 p-4 shadow-none">
        <CardHeader className="p-0 gap-1">
          <CardTitle className="text-sm font-bold text-ss-neutral-950 leading-4.5 ">
            Age Distribution
          </CardTitle>
          <CardDescription className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
            Age ranges of users engaging with your ads
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 p-0">
          {isLoading && (
            <div className="flex items-center justify-center py-36">
              <Loader className="animate-spin text-ss-neutral-500" />
            </div>
          )}
          {!isLoading && !isError && ageChartData.length > 0 && (
            <AgeBarChart data={ageChartData} />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
